import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { cameraConstraints, cameraErrorKind, nextCamera, pickRearCamera, videoInputs } from "./camera.js";
import { Icon } from "./Icon.js";
import { useTexts } from "./texts.js";
import { Button, Modal } from "./ui.js";
const stopAll = (stream) => stream?.getTracks().forEach((t) => t.stop());
/**
 * Take a photo with the device camera (0.10.2), for Windows tablets and
 * laptops where `capture="environment"` is ignored: a live preview of the
 * rear camera (facingMode "environment", then the camera whose label says
 * rear or back), a switch button when there is more than one camera, a
 * 56 px shutter, then "Retake" or "Use photo". The photo is a JPEG of the
 * full camera resolution; the app's own preparation (prepareImage) scales it.
 * Every track stops on close, on unmount and when the tab is hidden. Denied,
 * no camera and busy each have a text and the file picker as fallback. A
 * native <dialog> (Modal): focus stays inside, Esc closes.
 */
export function CameraCapture({ open, onClose, onCapture, onFallback, title, }) {
    const t = useTexts();
    const video = useRef(null);
    const stream = useRef(null);
    const devices = useRef([]);
    const [deviceId, setDeviceId] = useState(null);
    const [cameras, setCameras] = useState(0);
    const [phase, setPhase] = useState({ s: "starting" });
    const run = useRef(0);
    const stop = useCallback(() => {
        run.current++;
        stopAll(stream.current);
        stream.current = null;
        if (video.current)
            video.current.srcObject = null;
    }, []);
    const start = useCallback(async (wanted) => {
        stop();
        const my = run.current;
        setPhase({ s: "starting" });
        const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
        if (!md?.getUserMedia) {
            setPhase({ s: "error", kind: "none" });
            return;
        }
        try {
            let s = await md.getUserMedia(cameraConstraints(wanted));
            if (my !== run.current)
                return stopAll(s);
            // labels are known now: a camera that says rear wins over facingMode
            const list = (await md.enumerateDevices().catch(() => []));
            devices.current = list;
            setCameras(videoInputs(list).length);
            let current = s.getVideoTracks()[0]?.getSettings?.().deviceId ?? wanted;
            const rear = wanted ? null : pickRearCamera(list);
            if (rear && rear !== current) {
                stopAll(s);
                s = await md.getUserMedia(cameraConstraints(rear));
                if (my !== run.current)
                    return stopAll(s);
                current = rear;
            }
            stream.current = s;
            setDeviceId(current ?? null);
            if (video.current) {
                video.current.srcObject = s;
                await video.current.play().catch(() => undefined);
            }
            setPhase({ s: "live" });
        }
        catch (e) {
            if (my === run.current)
                setPhase({ s: "error", kind: cameraErrorKind(e) });
        }
    }, [stop]);
    // start on open, stop on close and unmount
    useEffect(() => {
        if (!open) {
            stop();
            return;
        }
        void start(null);
        return stop;
    }, [open, start, stop]);
    // a hidden tab stops the camera; back on the tab, a live preview starts again
    useEffect(() => {
        if (!open)
            return;
        const h = () => {
            if (document.hidden)
                stop();
            else if (phase.s === "live" || phase.s === "starting")
                void start(deviceId);
        };
        document.addEventListener("visibilitychange", h);
        return () => document.removeEventListener("visibilitychange", h);
    }, [open, phase.s, deviceId, start, stop]);
    // the URL of a taken photo is released when it is replaced or the dialog closes
    useEffect(() => {
        const url = phase.s === "taken" ? phase.url : null;
        return () => {
            if (url)
                URL.revokeObjectURL(url);
        };
    }, [phase]);
    const shoot = () => {
        const v = video.current;
        if (!v || !v.videoWidth)
            return;
        const c = document.createElement("canvas");
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        c.getContext("2d")?.drawImage(v, 0, 0, c.width, c.height);
        c.toBlob((blob) => {
            if (!blob)
                return;
            stop();
            setPhase({ s: "taken", blob, url: URL.createObjectURL(blob) });
        }, "image/jpeg", 0.92);
    };
    // the file picker opens once the modal dialog is closed (while it is open
    // the rest of the page is inert and a click on the app's input is ignored)
    const fallback = () => {
        onClose();
        if (onFallback)
            setTimeout(onFallback, 60);
    };
    const use = () => {
        if (phase.s !== "taken")
            return;
        const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15);
        onCapture(new File([phase.blob], `photo-${stamp}.jpg`, { type: "image/jpeg" }));
        onClose();
    };
    const errorText = phase.s === "error"
        ? phase.kind === "denied"
            ? t.cameraDenied
            : phase.kind === "none"
                ? t.cameraNone
                : phase.kind === "busy"
                    ? t.cameraBusy
                    : t.cameraFailed
        : "";
    const status = phase.s === "starting" ? t.cameraStarting : phase.s === "live" ? t.cameraLive : phase.s === "taken" ? t.cameraTaken : errorText;
    const switchTo = nextCamera(devices.current, deviceId);
    return (_jsx(Modal, { open: open, onClose: onClose, title: title ?? t.cameraTitle, width: 760, footer: phase.s === "taken" ? (_jsxs(_Fragment, { children: [_jsx(Button, { icon: "refresh", onClick: () => void start(deviceId), children: t.cameraRetake }), _jsx(Button, { variant: "primary", icon: "check", onClick: use, children: t.cameraUse })] })) : phase.s === "error" ? (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: onClose, children: t.close }), onFallback && (_jsx(Button, { variant: "primary", icon: "image", onClick: fallback, children: t.cameraFallback }))] })) : (_jsxs(_Fragment, { children: [onFallback && (_jsx(Button, { variant: "ghost", icon: "image", onClick: fallback, children: t.cameraFallback })), cameras > 1 && switchTo && (_jsx(Button, { icon: "refresh", onClick: () => void start(switchTo), disabled: phase.s !== "live", children: t.cameraSwitch }))] })), children: _jsxs("div", { className: "space-y-3", "data-camera-state": phase.s === "error" ? `error-${phase.kind}` : phase.s, children: [_jsx("p", { className: phase.s === "error" ? "rounded-xl bg-err-subtle px-3 py-2 text-sm text-err-fg" : "sr-only", role: phase.s === "error" ? "alert" : undefined, "aria-live": "polite", children: status }), phase.s !== "error" && (_jsxs("div", { className: "relative overflow-hidden rounded-2xl bg-black", children: [phase.s === "taken" ? (_jsx("img", { src: phase.url, alt: t.cameraTaken, className: "mx-auto max-h-[60dvh] w-full object-contain" })) : (_jsx("video", { ref: video, className: "mx-auto max-h-[60dvh] w-full object-contain", playsInline: true, muted: true, autoPlay: true, "aria-label": t.cameraPreview })), phase.s === "starting" && (_jsx("p", { className: "absolute inset-0 flex items-center justify-center text-sm text-white/80", "aria-hidden": "true", children: t.cameraStarting })), phase.s === "live" && (_jsx("div", { className: "absolute inset-x-0 bottom-3 flex justify-center", children: _jsx("button", { type: "button", onClick: shoot, "aria-label": t.cameraShutter, className: "flex h-16 w-16 items-center justify-center rounded-full bg-white text-brand shadow-2xl ring-4 ring-white/50 outline-none focus-visible:ring-accent", children: _jsx(Icon, { name: "camera", size: 26 }) }) }))] }))] }) }));
}
